import { UserRole } from '../types/enums'

/**
 * Default Cognito groups that should be created during system initialization
 */
export const DEFAULT_COGNITO_GROUPS = [
  {
    GroupName: UserRole.ADMINISTRADOR,
    Description: 'Full system access including user management',
    Precedence: 1,
  },
  {
    GroupName: UserRole.EDITOR,
    Description: 'Can create and manage job postings and evaluate candidates',
    Precedence: 2,
  },
  {
    GroupName: UserRole.VISUALIZADOR,
    Description: 'Read-only access to job postings and candidate information',
    Precedence: 3,
  },
] as const

/**
 * User status options for forms and filters
 */
export const USER_STATUS_OPTIONS = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'pending_confirmation', label: 'Pendiente de confirmación' },
] as const

/**
 * Default pagination settings for user management
 */
export const USER_MANAGEMENT_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 5,
} as const

/**
 * User management table column configurations
 */
export const USER_TABLE_COLUMNS = [
  { key: 'name', label: 'Nombre', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'role', label: 'Rol', sortable: true },
  { key: 'status', label: 'Estado', sortable: true },
  { key: 'last_login', label: 'Último acceso', sortable: true },
  { key: 'created_at', label: 'Fecha de creación', sortable: true },
  { key: 'actions', label: 'Acciones', sortable: false },
] as const

/**
 * Role management table column configurations
 */
export const ROLE_TABLE_COLUMNS = [
  { key: 'name', label: 'Nombre', sortable: true },
  { key: 'description', label: 'Descripción', sortable: false },
  { key: 'user_count', label: 'Usuarios', sortable: true },
  { key: 'is_system_role', label: 'Tipo', sortable: true },
  { key: 'created_at', label: 'Fecha de creación', sortable: true },
  { key: 'actions', label: 'Acciones', sortable: false },
] as const

/**
 * Permission resource labels for UI display
 */
export const PERMISSION_RESOURCE_LABELS = {
  users: 'Usuarios',
  roles: 'Roles',
  solicitudes: 'Solicitudes',
  postulaciones: 'Postulaciones',
  reports: 'Reportes',
} as const

/**
 * Permission action labels for UI display
 */
export const PERMISSION_ACTION_LABELS = {
  create: 'Crear',
  read: 'Ver',
  update: 'Editar',
  delete: 'Eliminar',
  evaluate: 'Evaluar',
  update_status: 'Cambiar estado',
  view: 'Ver',
  export: 'Exportar',
  qualify: 'Calificar',
  bulk_qualify: 'Calificar en lote',
} as const

/**
 * Form validation rules for user management
 */
export const USER_FORM_VALIDATION = {
  EMAIL: {
    REQUIRED: true,
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MAX_LENGTH: 255,
  },
  NAME: {
    REQUIRED: true,
    MIN_LENGTH: 2,
    MAX_LENGTH: 100,
    PATTERN: /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s\-'\.]+$/,
  },
  PHONE: {
    REQUIRED: false,
    PATTERN: /^[\+]?[1-9][\d]{0,15}$/,
  },
  DEPARTMENT: {
    REQUIRED: false,
    MAX_LENGTH: 100,
  },
  POSITION: {
    REQUIRED: false,
    MAX_LENGTH: 100,
  },
} as const

/**
 * Form validation rules for role management
 */
export const ROLE_FORM_VALIDATION = {
  NAME: {
    REQUIRED: true,
    MIN_LENGTH: 3,
    MAX_LENGTH: 50,
    PATTERN: /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/,
  },
  DESCRIPTION: {
    REQUIRED: true,
    MIN_LENGTH: 10,
    MAX_LENGTH: 255,
  },
} as const

/**
 * API endpoints for user management
 */
export const USER_MANAGEMENT_ENDPOINTS = {
  USERS: '/api/users',
  USER_BY_ID: (id: string) => `/api/users/${id}`,
  ROLES: '/api/roles',
  ROLE_BY_ID: (id: string) => `/api/roles/${id}`,
} as const

/**
 * Error messages for user management operations
 */
export const USER_MANAGEMENT_MESSAGES = {
  SUCCESS: {
    USER_CREATED: 'Usuario creado exitosamente',
    USER_UPDATED: 'Usuario actualizado exitosamente',
    USER_DELETED: 'Usuario eliminado exitosamente',
    ROLE_CREATED: 'Rol creado exitosamente',
    ROLE_UPDATED: 'Rol actualizado exitosamente',
    ROLE_DELETED: 'Rol eliminado exitosamente',
  },
  ERROR: {
    GENERIC: 'Ha ocurrido un error inesperado',
    NETWORK: 'Error de conexión. Verifica tu conexión a internet',
    UNAUTHORIZED: 'No tienes permisos para realizar esta acción',
    VALIDATION: 'Por favor corrige los errores en el formulario',
  },
  CONFIRMATION: {
    DELETE_USER: '¿Estás seguro de que deseas eliminar este usuario?',
    DELETE_ROLE: '¿Estás seguro de que deseas eliminar este rol?',
    DEACTIVATE_USER: '¿Estás seguro de que deseas desactivar este usuario?',
  },
} as const

/**
 * Loading states for user management operations
 */
export const LOADING_STATES = {
  LOADING_USERS: 'Cargando usuarios...',
  LOADING_ROLES: 'Cargando roles...',
  CREATING_USER: 'Creando usuario...',
  UPDATING_USER: 'Actualizando usuario...',
  DELETING_USER: 'Eliminando usuario...',
  CREATING_ROLE: 'Creando rol...',
  UPDATING_ROLE: 'Actualizando rol...',
  DELETING_ROLE: 'Eliminando rol...',
} as const

/**
 * Default values for forms
 */
export const FORM_DEFAULTS = {
  USER: {
    send_invitation: true,
    status: 'active' as const,
    role: UserRole.VISUALIZADOR,
  },
  ROLE: {
    is_system_role: false,
  },
} as const
